import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Formik, Form } from 'formik'
import { PlusSquareFill, XLg } from 'react-bootstrap-icons'
import CreatableSelect from 'react-select/creatable';
import axiosService from '../../../utils/AxiosService'
import { useParams } from 'react-router-dom';
import { toastSuccess, toastError } from '../../FormComponents/Toasts';
import { isUgcCreatorTag } from '../../../utils/tagStyles';
import { useReadOnly } from '../../../contexts/ReadOnlyContext';
import { findExactTag, findSimilarTags } from '../../../utils/tagSimilarity';

export default observer(function CampaignInfluencerTags({ auth, currentTags, arePublic }) {
  const readOnly = useReadOnly()
  const [tagOptions, setTagOptions] = useState([])
  const [scopeTags, setScopeTags] = useState([])
  const [isFocused, setIsFocused] = useState(false)
  const { id } = useParams()

  const tagBgColor = arePublic ? '#EFF6FF' : '#FEFCE8'
  const tagTxtColor = arePublic ? '#2563EB' : '#EAB308'

  const customAriaLiveMessages = {
    guidance: () => '',
    onFocus: () => '',
    onFilter: () => '',
    onCreateOption: () => '',
    onSelect: () => '',
    onInputChange: () => '',
    onClose: () => '',
    onOpen: () => '',
    onClear: () => '',
    onRemove: () => '',
  };

  const getTags = () => {
    axiosService.get('/api/project_manager/tags')
      .then((tags) => {
        const allTagsList = arePublic ? tags.filter(tag => tag.campaign_id === null) : tags.filter(tag => tag.campaign_id === +id)
        const currentIds = currentTags.map(tag => tag.id)
        const optionSet = allTagsList.filter(tag => !currentIds.includes(tag.id))
        setScopeTags(allTagsList)

        setTagOptions(optionSet.map(tag => ({ label: tag.name, value: tag.id })))
      })
  }

  useEffect(() => getTags(), [currentTags])

  const removeTag = tag => () => {
    axiosService.delete(`/api/project_manager/authentications/${auth.authentication_id}/tags/${tag.id}`)
      .then(deletedTag => {
        toastSuccess('Tag Removed')
        auth.tags = auth.tags.filter(tag => tag.id !== deletedTag.id)
      })
      .catch(({ data: { error } }) => toastError(error))
  }

  const handleAddTag = ({ tag_id, tag_name }) => {
    if (tag_id) {
      axiosService.post(`/api/project_manager/authentications/${auth.authentication_id}/tags/${tag_id}/add_to_authentication`)
        .then(tag => {
          toastSuccess('Tag Added')
          if (!currentTags.some(assigned => assigned.id === tag.id)) currentTags.push(tag)
          if (!auth.tags.some(assigned => assigned.id === tag.id)) auth.tags.push(tag)
          getTags()
        })
        .catch(({ data: { error } }) => toastError(error))
    } else {
      const tagObject = arePublic ? { tag: { name: tag_name } } : { tag: { name: tag_name, campaign_id: id } }
      axiosService.post(`/api/project_manager/authentications/${auth.authentication_id}/tags/`, tagObject)
        .then(tag => {
          toastSuccess('Tag Added')
          if (!currentTags.some(assigned => assigned.id === tag.id)) currentTags.push(tag)
          if (!auth.tags.some(assigned => assigned.id === tag.id)) auth.tags.push(tag)
          getTags()
        })
        .catch(({ data: { error } }) => toastError(error))
    }
  }

  const handleFocus = () => setIsFocused(true);
  const handleBlurr = () => setIsFocused(false);

  return (
    <div>
      <div className="my-4 flex gap-2 flex-wrap">
        {
          currentTags.map((tag) => {
            const ugc = isUgcCreatorTag(tag)
            const bgColor = ugc ? '#F5F3FF' : tagBgColor
            const txtColor = ugc ? '#7C3AED' : tagTxtColor
            return (
              <span key={tag.id} title={ugc ? 'UGC creator - applies to all of this creator\'s accounts' : undefined} className='px-3 py-1 rounded-full flex items-center gap-1 w-fit' style={{ backgroundColor: bgColor }}>
                <span style={{ color: txtColor }}>{ugc ? `★ ${tag.name}` : tag.name}</span>
                {!readOnly && <button onClick={removeTag(tag)}><span><XLg className='text-xs font-bold' style={{ color: txtColor }} /></span></button>}
              </span>
            )
          })
        }
      </div>
      {!readOnly && <div className="flex">
        <Formik
          initialValues={{
            tag_name: '',
            tag_id: null,
          }}
          onSubmit={handleAddTag}
        >
          {({values, setFieldValue}) => (
            <Form className='flex flex-col justify-center w-full'>
              <div className="flex gap-2 items-center">
                <div className='flex-1'>
                  <CreatableSelect
                    classNamePrefix="react-select"
                    isClearable
                    name="tag_id"
                    ariaLiveMessages={customAriaLiveMessages}
                    placeholder={isFocused ? "" : "Add your tag"}
                    options={tagOptions}
                    onBlur={handleBlurr}
                    onFocus={handleFocus}
                    onChange={tag => setFieldValue('tag_id', tag?.value, true)}
                    isValidNewOption={inputValue => !!inputValue.trim() && !findExactTag(inputValue, scopeTags)}
                    onCreateOption={(inputValue) => {
                      const currentIds = currentTags.map(tag => tag.id)
                      const similar = findSimilarTags(inputValue, scopeTags.filter(tag => !currentIds.includes(tag.id)))
                      if (similar.length && !window.confirm(`Similar tags already exist: ${similar.map(tag => tag.name).join(', ')}. Create "${inputValue.trim()}" anyway?`)) return
                      setFieldValue('tag_id', null, true)
                      setFieldValue('tag_name', inputValue, true)
                    }}
                    value={values.tag_id}
                  />
                </div>
                <button
                  type="submit"
                  className="submit"
                >
                  <PlusSquareFill className='text-rb-green h-10 w-10'/>
                </button>
              </div>
            </Form>
          )}
        </Formik>

      </div>}
    </div>
  )
})
